import { Position } from "geojson";

import { MuPowerOutputLayerIds, MuPowerSolverLayerIds } from "fond/types";

import { ValidationFunction, ValidationHelpers } from "./GenericValidator";

/** Loop connection phase options for each supported wire size. */
export const LOOP_CONNECTION_PHASES_BY_WIRE_SIZE: Record<string, string[]> = {
  "3x_1/0_Al": ["ABC", "X"],
  "2x_1/0_Al": ["AB", "AC", "BC", "X"],
  "1/0_Al": ["A", "B", "C", "X"],
};

const TWO_PHASE_PAIRS = new Set(["AB", "AC", "BC"]);

const isTruthyFlag = (value: unknown): boolean => value === "True" || value === true;

/**
 * A position is a valid loop connection endpoint if it sits on a source hub, a transformer hub, or a
 * primary joint box (a feature in the boxes layer whose TYPE is "Primary Joint Box").
 */
const isAllowedEndpoint = (position: Position, helpers: ValidationHelpers): boolean =>
  helpers
    .findFeaturesAtPosition(position, [MuPowerSolverLayerIds.sourceHubs, MuPowerSolverLayerIds.transformerHubs, MuPowerOutputLayerIds.boxes])
    .some((f) => f.properties?.layerId !== MuPowerOutputLayerIds.boxes || f.properties?.TYPE === "Primary Joint Box");

/**
 * The two-phase pairs (e.g. "AB") of the radial primary wires colocated with a position.
 */
const twoPhasePairsAtPosition = (position: Position, helpers: ValidationHelpers): string[] =>
  helpers
    .findFeaturesAtPosition(position, MuPowerOutputLayerIds.primaryWires)
    .filter((wire) => !isTruthyFlag(wire.properties?.IsLoop) && !isTruthyFlag(wire.properties?.IsVirtual))
    .map((wire) => wire.properties?.Phase)
    .filter((pair): pair is string => typeof pair === "string" && TWO_PHASE_PAIRS.has(pair));

/**
 * A loop connection phase is compatible with a two-phase if the phase is a "subset" of the pair.
 *
 * For example, the phase "A" is compatible with pairs "AB" or "AC", but not "BC". The phase "ABC" is treated
 * as being compatible with all pairs, even though I have no idea what that would mean in practice.
 */
const isPhaseCompatibleWithPair = (phase: string, pair: string): boolean => phase === "ABC" || [...phase].every((letter) => pair.includes(letter));

interface MakeValidateLoopConnectionOptions {
  /** Wire sizes enabled in the version's power architecture. */
  allowedWireSizes: string[];
}

/**
 * Factory for a loop connection validator.
 *
 * A loop connection is valid if:
 *
 * - Its first and last vertices each sit on a source hub, transformer, or primary joint box.
 * - It has a valid wire size, the wire size is one of `allowedWireSizes`, and its phase is valid
 *   for the selected wire size.
 * - The phase selected is compatible with any two-phase primary wires at its endpoints.
 *
 * We currently do not validate that the phase is compatible with any _one-phase_ primary wires
 * at the loop connection's endpoints because the solver doesn't care.
 */
export const makeValidateLoopConnection =
  ({ allowedWireSizes }: MakeValidateLoopConnectionOptions): ValidationFunction =>
  (feature, _allFeatures, helpers) => {
    const featureId = feature.id as string;

    if (feature.geometry.type !== "LineString") {
      return { featureId, valid: false, errors: ["Loop connection must be a LineString"] };
    }

    const coords = feature.geometry.coordinates;
    if (coords.length < 2) {
      return { featureId, valid: false, errors: ["Loop connection must have at least 2 vertices"] };
    }

    const firstVertex = coords[0];
    const lastVertex = coords[coords.length - 1];

    const errors: string[] = [];

    if (helpers.isColocated(firstVertex, lastVertex)) errors.push("Loop connection cannot have the same start and end point");
    if (!isAllowedEndpoint(firstVertex, helpers)) errors.push("Loop connection must start at a source hub, transformer, or primary joint box");
    if (!isAllowedEndpoint(lastVertex, helpers)) errors.push("Loop connection must end at a source hub, transformer, or primary joint box");

    const { WireSize: wireSize, Phase: phase } = feature.properties ?? {};
    const allowedPhases = typeof wireSize === "string" ? LOOP_CONNECTION_PHASES_BY_WIRE_SIZE[wireSize] : undefined;
    if (!allowedPhases) {
      errors.push("Loop connection must have a known wire size");
    } else if (!allowedWireSizes.includes(wireSize)) {
      errors.push(`Primary wire size ${wireSize} is not enabled in the architecture`);
    } else if (phase != null && !allowedPhases.includes(phase)) {
      errors.push(`Phase ${phase} is not valid for wire size ${wireSize}`);
    } else if (typeof phase === "string" && phase !== "X") {
      // 2-phase compatibility check: if the start/end of the loop connection are two-phase, then the solver
      // places strict limits on what phases can be used for the loop connection.
      const pairs = new Set([...twoPhasePairsAtPosition(firstVertex, helpers), ...twoPhasePairsAtPosition(lastVertex, helpers)]);
      pairs.forEach((pair) => {
        if (!isPhaseCompatibleWithPair(phase, pair)) {
          errors.push(`Phase ${phase} is incompatible with the two-phase wire (${pair}) it connects to`);
        }
      });
    }

    return { featureId, valid: errors.length === 0, errors };
  };
